import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { Checkbox, FormControlLabel } from "@mui/material";

import { Notice, NoticeText } from "components/ui/Notice";
import { FIELDS_NOT_REQUIRED_WHEN_GIVEN_ELSEWHERE } from "features/immunizations/utils/administeredElsewhere";

import style from "../AddVaccineForm.module.css";

/**
 * A dose somebody else gave — a school, a maternity unit, another country (KIN-1883).
 *
 * Until now a clinician had two ways to record one and both were wrong. Recording it
 * normally asserts that this practice administered the dose: it is reported to NIIS and it
 * raises a PCRS claim, under our own provider id. Recording it as a manual entry means "a
 * product with no catalogue code", and because a manual entry has no product it fills no
 * schedule slot — so the patient stays on the recall list for a vaccine they have already
 * had. So the honest option did not exist and the wrong one was the easy one.
 *
 * Says "patient", not "child": this form records adult vaccinations too, and a notice that
 * talks about a child's schedule on an adult's record reads as though the wrong thing is
 * being recorded (KIN-1964).
 *
 * A checkbox rather than the Yes/No radios used for the Healthcare Worker question: that
 * one is unanswered until it is answered, and an unticked box could not be told apart from
 * a deliberate No. Here unticked genuinely is the answer — this practice gave the dose,
 * which is the overwhelmingly common case.
 */
export const GivenElsewhereFields = () => {
  const { watch, setValue, clearErrors } = useFormContext();
  const isGivenElsewhere = watch("givenElsewhere");

  /*
   * Clear the errors the five fields may already be carrying when the box is ticked.
   * Without this a clinician who pressed Save, met "Enter the batch no.", and then ticked
   * the box is still looking at an error under a field that is no longer required — and
   * the message points at the very thing this option exists to stop asking for.
   */
  useEffect(() => {
    if (isGivenElsewhere) {
      clearErrors(FIELDS_NOT_REQUIRED_WHEN_GIVEN_ELSEWHERE);
    } else {
      setValue("givenElsewhereBy", "", { shouldDirty: true });
    }
  }, [isGivenElsewhere, clearErrors, setValue]);

  return (
    <div className={style.givenElsewhereRow}>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={!!isGivenElsewhere}
            inputProps={{ "data-testid": "given-elsewhere-checkbox" }}
            onChange={(event) =>
              setValue("givenElsewhere", event.target.checked, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            sx={{ "&.Mui-checked": { color: "var(--green-900)" } }}
          />
        }
        label="This dose was given somewhere else"
      />

      {/*
        Said plainly rather than left to be discovered. The consequences are not reversible
        by the clinician and not visible from the form: nothing goes to NIIS and nothing is
        claimed. The second sentence is the reason the product stays required, which
        otherwise looks like an oversight.
      */}
      {!!isGivenElsewhere && (
        <Notice>
          <NoticeText>
            This will not be reported to NIIS and no claim will be made — we
            cannot report a dose we did not give. Still choose the vaccine and the
            date: the vaccine is what fills the patient&apos;s schedule and takes
            them off the recall list, and the date drives the interval rules.
          </NoticeText>
        </Notice>
      )}
    </div>
  );
};
